import { useId, useMemo, useRef, useState } from 'react';
import { dismiss, notifyError, notifyProblems } from '../lib/errorNotices';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canEditBranding } from '@promanage/core';
import { logoCornerRadius } from '@promanage/core/brand';
import {
  BRAND_LOGO_LIMITS,
  EMAIL_FONT_KEYS,
  EMAIL_FOOTER_MAX_LENGTH,
  brandKitIssues,
  isEmailFont,
  type EmailFont,
} from '@promanage/core/brand';
import {
  EMAIL_FONTS,
  brandedEmail,
  emailFontStack,
  fitWithin,
} from '@promanage/documents/brand';
import { Button, Field, Input, Panel, PanelHeader, Select, Textarea } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SaveButton, useUnsavedChanges } from '../components/UnsavedChanges';
import { Checklist, ChecklistRow } from '../features/settings/Checklist';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useOrgSettings } from '../features/settings/org';
import { useSession } from '../features/auth/session';
import { prepareLogo, type PreparedLogo } from '../features/branding/prepareLogo';
import { PortalLinkPanel } from '../features/branding/PortalLinkPanel';
import { UploadRefused } from '../features/reports/prepareUpload';
import { needsStepUp, trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/settings/branding')({
  component: BrandingPage,
});

type BrandKit = RouterOutputs['branding']['get'];

/**
 * The agency's brand kit — the logo, typeface, colour and footer its emails carry.
 *
 * ── THE PREVIEW IS THE REAL RENDERER ─────────────────────────────────────────────────────────
 *
 * The email on the right is laid out by `brandedEmail`, the same function the worker sends with,
 * fed the unsaved values as they are typed. The words are a sample; the paint is not a mock-up of
 * the paint. What an administrator approves here is what four hundred owners receive.
 *
 * ── IT SAYS WHERE IT DOES NOT REACH ──────────────────────────────────────────────────────────
 *
 * The console keeps ProManage's design, and the bank-account change warning goes out plain. Both
 * are deliberate, and both are things an agency would otherwise assume the other way — so "Where
 * it appears" lists them beside the controls, a line each, rather than leaving them to be
 * discovered in an inbox.
 */

/** One notice for the chosen logo's own problems, replaced when another is chosen. */
const LOGO_PROBLEM = 'branding-logo';
function BrandingPage() {
  const kit = useQuery(
    trpc.branding.get.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load this agency’s branding' },
      staleTime: 5 * 60_000,
    }),
  );
  const org = useOrgSettings();
  const session = useSession();
  const mayChange = canEditBranding(session?.roles ?? []);
  const error = kit.error ?? org.error;

  if (!error && kit.data && org.data)
    return (
      <BrandKitForm
        kit={kit.data}
        orgName={org.data.name}
        senderName={org.data.statementDelivery.senderName ?? org.data.name}
        replyTo={org.data.replies.address}
        mayChange={mayChange}
      />
    );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="Branding" subtitle="How your emails and documents look" />

      <SettingsNav />

      {error ? (
        <LoadFailed
          retry={() => {
            void kit.refetch();
            void org.refetch();
          }}
          retrying={kit.isFetching || org.isFetching}
        />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-5" aria-hidden="true">
          <div className="h-[34rem] animate-pulse rounded-2xl bg-surface-sunk lg:col-span-3" />
          <div className="h-[34rem] animate-pulse rounded-2xl bg-surface-sunk lg:col-span-2" />
        </div>
      )}
    </div>
  );
}

/** What is happening to the logo in this edit — kept as stored, replaced, or taken off. */
type LogoChange = { kind: 'kept' } | { kind: 'new'; logo: PreparedLogo } | { kind: 'removed' };

function BrandKitForm({
  kit,
  orgName,
  senderName,
  replyTo,
  mayChange,
}: {
  kit: BrandKit;
  orgName: string;
  senderName: string;
  replyTo: string | null;
  mayChange: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const formId = useId();

  const [logoChange, setLogoChange] = useState<LogoChange>({ kind: 'kept' });
  const [preparing, setPreparing] = useState(false);
  const [font, setFont] = useState<EmailFont>(kit.emailFont);
  const [accent, setAccent] = useState(kit.accentColour ?? '');
  const [footer, setFooter] = useState(kit.emailFooter ?? '');
  const [logoOnDocuments, setLogoOnDocuments] = useState(kit.logoOnDocuments);
  const [logoRounded, setLogoRounded] = useState(kit.logoRounded);
  const [attempted, setAttempted] = useState(false);

  const logo =
    logoChange.kind === 'new' ? logoChange.logo : logoChange.kind === 'removed' ? null : kit.logo;

  // Normalised the way the server will store them, so "changed" means changed for an owner.
  const accentValue = accent.trim().toLowerCase();
  const footerValue = footer.replace(/\r\n?/g, '\n').trim();
  const accentOrNull = accentValue === '' ? null : accentValue;
  const footerOrNull = footerValue === '' ? null : footerValue;

  // Core's own rules, run as the operator types — the same sentences the server would refuse with.
  const colourIssue =
    accentOrNull === null
      ? undefined
      : brandKitIssues({ accentColour: accentOrNull, emailFooter: null })[0];
  const footerIssue =
    footerOrNull === null
      ? undefined
      : brandKitIssues({ accentColour: null, emailFooter: footerOrNull })[0];

  const dirty =
    logoChange.kind !== 'kept' ||
    font !== kit.emailFont ||
    accentOrNull !== kit.accentColour ||
    footerOrNull !== kit.emailFooter ||
    logoOnDocuments !== kit.logoOnDocuments ||
    logoRounded !== kit.logoRounded;
  const unsaved = useUnsavedChanges(mayChange && dirty);

  const save = useMutation(
    trpc.branding.save.mutationOptions({
      meta: { errorTitle: 'Could not save the branding' },
      async onSuccess(saved) {
        // An older read must not replace what just finished saving.
        await queryClient.cancelQueries({ queryKey: trpc.branding.get.queryKey() });
        queryClient.setQueryData(trpc.branding.get.queryKey(), saved);
        // The next PDF exported must carry this logo, not one cached five minutes ago.
        void queryClient.invalidateQueries({ queryKey: trpc.branding.letterhead.queryKey() });

        setLogoChange({ kind: 'kept' });
        setFont(saved.emailFont);
        setAccent(saved.accentColour ?? '');
        setFooter(saved.emailFooter ?? '');
        setLogoOnDocuments(saved.logoOnDocuments);
        setLogoRounded(saved.logoRounded);
        setAttempted(false);
        unsaved.saved();
      },
      onError(error) {
        // Fifteen minutes since the last code. Not something to explain in a red box.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  const busy = preparing || save.isPending;

  async function choose(file: File) {
    setPreparing(true);
    dismiss(LOGO_PROBLEM);
    save.reset();
    try {
      setLogoChange({ kind: 'new', logo: await prepareLogo(file) });
    } catch (cause) {
      // An UploadRefused is written for the person holding the file — too large, not an image.
      if (cause instanceof UploadRefused) {
        notifyProblems('Could not use that logo', [cause.reason], LOGO_PROBLEM);
      } else {
        notifyError(cause, 'Could not prepare that logo', { id: LOGO_PROBLEM });
      }
    } finally {
      setPreparing(false);
    }
  }

  function discard() {
    setLogoChange({ kind: 'kept' });
    setFont(kit.emailFont);
    setAccent(kit.accentColour ?? '');
    setFooter(kit.emailFooter ?? '');
    setLogoOnDocuments(kit.logoOnDocuments);
    setLogoRounded(kit.logoRounded);
    setAttempted(false);
    dismiss(LOGO_PROBLEM);
    save.reset();
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (colourIssue !== undefined || footerIssue !== undefined) return;

    save.mutate({
      ...(logoChange.kind === 'new'
        ? { logo: { base64: logoChange.logo.base64 } }
        : logoChange.kind === 'removed'
          ? { logo: null }
          : {}),
      emailFont: font,
      accentColour: accentOrNull,
      emailFooter: footerOrNull,
      logoOnDocuments,
      logoRounded,
    });
  };

  const preview = useMemo(() => {
    const email = brandedEmail({
      text: sampleNote(senderName),
      agencyName: senderName,
      brand: {
        font,
        accentColour: colourIssue === undefined ? accentOrNull : null,
        footer: footerOrNull,
      },
      logo:
        logo === null
          ? null
          : { src: logo.dataUrl, width: logo.width, height: logo.height, rounded: logoRounded },
      attachmentName: 'statement-2026-08-OW000012.pdf',
    });
    return `<!doctype html><html><body style="margin:0;padding:20px 22px;background:#fff">${email.html}</body></html>`;
  }, [senderName, font, accentOrNull, colourIssue, footerOrNull, logo, logoRounded]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      {/*
       * SAVE IN THE TOP BAR, reaching the form by its id, so it is in view beside the preview
       * rather than under three panels. Saving asks for the authenticator code when the last one
       * is over fifteen minutes old (`needsStepUp` above), and every change is audit-logged with
       * who made it — the footer's words before and after, the logo by fingerprint.
       */}
      <TopBar
        title="Branding"
        subtitle={mayChange ? orgName : `${orgName} · only an administrator can change this`}
        actions={
          mayChange ? (
            <>
              {dirty ? (
                <Button size="sm" variant="secondary" disabled={busy} onClick={discard}>
                  Discard
                </Button>
              ) : null}
              <SaveButton
                size="sm"
                form={formId}
                pending={save.isPending}
                saved={unsaved.justSaved}
                disabled={!dirty || preparing}
              >
                Save
              </SaveButton>
            </>
          ) : null
        }
      />
      <SettingsNav />

      <form
        id={formId}
        onSubmit={submit}
        noValidate
        className="grid items-start gap-4 lg:grid-cols-5"
      >
        <div className="flex flex-col gap-4 lg:col-span-3">
          <Panel className="p-5">
            <PanelHeader title="Logo" subtitle="Heads every email" className="p-0 pb-4" />

            <div className="flex flex-wrap items-center gap-4">
              {/* White, not the console's ground: this is the colour the logo will be seen on. */}
              <div className="flex h-24 w-60 max-w-full shrink-0 items-center justify-center rounded-lg bg-white p-3 ring-1 ring-rule">
                {logo ? (
                  <img
                    src={logo.dataUrl}
                    alt={
                      logoChange.kind === 'new' ? 'New logo, not yet saved' : `${senderName} logo`
                    }
                    className="max-h-full max-w-full object-contain"
                    // The unsaved choice, at the size this box shows the logo — the same rule the
                    // email beside it and every document use.
                    style={
                      logoRounded
                        ? {
                            borderRadius: `${String(
                              Math.round(
                                logoCornerRadius(fitWithin(logo, { width: 216, height: 72 })),
                              ),
                            )}px`,
                          }
                        : undefined
                    }
                  />
                ) : (
                  <span className="text-[0.75rem] text-ink-subtle">
                    {logoChange.kind === 'removed' ? 'Removed when you save' : 'No logo'}
                  </span>
                )}
              </div>

              {/* A floor on the width so a phone wraps this under the logo rather than squeezing it. */}
              <div className="min-w-48 flex-1">
                {/* Reduced on the device before upload, transparency kept. SVG is refused by
                    `prepareLogo` with its own sentence — mail apps do not render it reliably. */}
                <p className="text-[0.75rem] text-ink-muted tabular-nums">
                  {logo
                    ? `${String(logo.width)} × ${String(logo.height)} px${logoChange.kind === 'new' ? ' · not saved' : ''}`
                    : `PNG, JPEG or WebP · fitted to ${String(BRAND_LOGO_LIMITS.maxWidth)} × ${String(BRAND_LOGO_LIMITS.maxHeight)} px`}
                </p>

                {mayChange ? (
                  <>
                    <input
                      ref={fileInput}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      aria-label="Choose a logo file"
                      className="hidden"
                      disabled={busy}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = '';
                        if (file) void choose(file);
                      }}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => fileInput.current?.click()}
                      >
                        {preparing ? 'Preparing' : logo ? 'Replace' : 'Upload logo'}
                      </Button>
                      {logo ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            dismiss(LOGO_PROBLEM);
                            setLogoChange(
                              kit.logo === null ? { kind: 'kept' } : { kind: 'removed' },
                            );
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                      {logoChange.kind !== 'kept' ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => setLogoChange({ kind: 'kept' })}
                        >
                          Undo
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {/*
             * On a document the logo takes the place of the agency's name at the top right;
             * ProManage's mark stays at the top left, as the software that produced the figures.
             */}
            <label className="mt-4 flex items-start gap-2 border-t border-rule pt-4 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={logoOnDocuments}
                onChange={(event) => setLogoOnDocuments(event.target.checked)}
                disabled={!mayChange}
                className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span className="min-w-0">
                Also on PDF documents
                <span className="block text-[0.75rem] text-ink-muted">
                  {logoOnDocuments && logo === null
                    ? 'Takes effect once a logo is uploaded'
                    : 'Statements, remittances, inspection reports and other reports'}
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-2 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={logoRounded}
                onChange={(event) => setLogoRounded(event.target.checked)}
                disabled={!mayChange}
                className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span className="min-w-0">
                Round the corners
                <span className="block text-[0.75rem] text-ink-muted">
                  Everywhere the logo appears: emails, PDF documents and the client portal
                </span>
              </span>
            </label>
          </Panel>

          <Panel className="p-5">
            <PanelHeader title="Email style" className="p-0 pb-4" />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                {/* Only faces already on readers' devices: mail apps load no others, so a custom
                    font would quietly fall back. */}
                <Field label="Typeface" markOptional={false} hint="Fonts every mail app has">
                  {(props) => (
                    <Select
                      {...props}
                      value={font}
                      disabled={!mayChange}
                      onChange={(event) => {
                        if (isEmailFont(event.target.value)) setFont(event.target.value);
                      }}
                    >
                      {EMAIL_FONT_KEYS.map((key) => (
                        <option key={key} value={key}>
                          {EMAIL_FONTS[key].label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <p
                  className="rounded-md bg-surface-sunk px-3 py-2 text-[0.9375rem]"
                  style={{ fontFamily: emailFontStack(font) }}
                >
                  Kia ora — your statement is attached.
                </p>
              </div>

              {/* A rule under the logo, never text, so any colour stays readable. */}
              <Field
                label="Brand colour"
                hint="A line under the logo"
                error={attempted || accentValue.length >= 7 ? colourIssue : undefined}
              >
                {(props) => (
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label="Pick the brand colour"
                      value={
                        accentOrNull !== null && colourIssue === undefined
                          ? accentOrNull
                          : '#1d4ed8'
                      }
                      onChange={(event) => setAccent(event.target.value)}
                      disabled={!mayChange}
                      className="h-9 w-11 shrink-0 cursor-pointer rounded-md bg-surface p-1 ring-1 ring-rule disabled:cursor-not-allowed"
                    />
                    <Input
                      {...props}
                      value={accent}
                      onChange={(event) => setAccent(event.target.value)}
                      placeholder="None"
                      maxLength={7}
                      spellCheck={false}
                      disabled={!mayChange}
                      className="font-mono"
                    />
                    {accent !== '' && mayChange ? (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setAccent('')}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                )}
              </Field>

              <div className="flex flex-col gap-1 sm:col-span-2">
                <Field label="Footer" hint="Address, phone or licence line" error={footerIssue}>
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={3}
                      value={footer}
                      onChange={(event) => setFooter(event.target.value)}
                      maxLength={EMAIL_FOOTER_MAX_LENGTH}
                      disabled={!mayChange}
                      placeholder={`${senderName}\n12 Queen Street, Auckland 1010\n09 555 0123`}
                    />
                  )}
                </Field>
                <p className="text-right text-[0.6875rem] text-ink-subtle tabular-nums">
                  {footer.length} / {EMAIL_FOOTER_MAX_LENGTH}
                </p>
              </div>
            </div>
          </Panel>

          <Panel className="p-5">
            <PanelHeader title="Where it appears" className="p-0 pb-3" />
            {/*
             * WHERE IT DOES NOT REACH is stated as plainly as where it does: an agency would assume
             * both of these the other way. The bank-account change warning goes out plain because
             * it is the one message that has to be trusted over anything else in an inbox —
             * including a footer. The logo is embedded rather than fetched, so it shows without the
             * reader allowing images and nobody learns when an email was opened.
             */}
            <Checklist>
              <ChecklistRow yes label="Statement emails">
                Everything
              </ChecklistRow>
              <ChecklistRow yes label="Automation emails">
                Everything
              </ChecklistRow>
              <ChecklistRow yes={logoOnDocuments} label="PDF documents">
                {logoOnDocuments ? 'Logo only' : 'Nothing — switched off'}
              </ChecklistRow>
              <ChecklistRow yes={false} label="Bank account change warnings">
                Sent plain, for trust
              </ChecklistRow>
              <ChecklistRow yes label="Client portal sign-in page">
                Logo, name and colour
              </ChecklistRow>
              <ChecklistRow yes={false} label="This console">
                Keeps ProManage’s design
              </ChecklistRow>
            </Checklist>
          </Panel>

          <PortalLinkPanel mayChange={mayChange} />
        </div>

        <div className="lg:sticky lg:top-24 lg:col-span-2">
          <Panel className="overflow-hidden p-0">
            {/* The real renderer (`brandedEmail`) fed the unsaved values — sample words, real
                paint. Mail apps differ a little in spacing, never in what the email says. */}
            <PanelHeader title="Preview" subtitle="A statement email" className="px-5 pt-5 pb-3" />
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 border-t border-rule px-5 py-3 text-[0.75rem]">
              <dt className="text-ink-subtle">From</dt>
              <dd className="truncate font-medium">{senderName}</dd>
              <dt className="text-ink-subtle">Reply to</dt>
              <dd className="truncate">{replyTo ?? 'Not set — see Delivery'}</dd>
              <dt className="text-ink-subtle">Subject</dt>
              <dd className="truncate">Your statement for August 2026</dd>
            </dl>
            {/* No scripts, no same-origin: the preview is markup to look at and nothing more. */}
            <iframe
              title="Preview of a statement email in this branding"
              sandbox=""
              srcDoc={preview}
              className="block h-[32rem] w-full border-t border-rule bg-white"
            />
          </Panel>
        </div>
      </form>
    </div>
  );
}

/**
 * The words in the preview — a stand-in for a covering note. The layout around them is
 * `brandedEmail` itself; the real note is written from a real statement when one is produced.
 */
function sampleNote(agencyName: string): string {
  return [
    'Kia ora Alex Taylor,',
    '',
    'Statement 12, covering August 2026, is attached.',
    '',
    'Paid out to you: $2,140.00',
    'Held for you at 31 August 2026: $350.00',
    '',
    'The attachment shows every receipt and charge, one section per property, and how those' +
      ' figures reconcile.',
    '',
    'If anything looks wrong, reply to this email and quote Statement 12 — we will look into it.',
    '',
    agencyName,
  ].join('\n');
}
